import React, { useState } from 'react'
import { Switch } from '@headlessui/react'
import { useDispatch, useSelector } from 'react-redux'
import DateField from '../../elements/form/DateField'
import ModalActionButton from '../../elements/ModalActionButton'
import NumberField from '../../elements/form/NumberField'
import PlusIcon from '../../elements/icons/PlusIcon'
import SelectField from '../../elements/form/SelectField'
import TextField from '../../elements/form/TextField'
import TextareaField from '../../elements/form/TextareaField'
import TrashIcon from '../../elements/icons/TrashIcon'
import { createSickBayRecord } from '../../../store/actions/sickbayActions'
import { ERROR } from '../../../store/types'

const todayAsInputDate = () => new Date().toISOString().slice(0, 10)

const NewSickbayVisit = ({ student, close }) => {
    const dispatch = useDispatch()
    const sickbaySelector = useSelector((state) => state.sickbay)

    const [visitPayload, setVisitPayload] = useState({
        visitDate: todayAsInputDate(),
        outcome: 'treated-and-returned',
        symptoms: [''],
        complaints: '',
        diagnosis: '',
        treatmentPlan: '',
        vitals: {
            temperature: undefined,
            pulseRate: undefined,
            bloodPressure: '',
            weight: undefined,
            notes: ''
        },
        medicationsDispensed: [],
        admission: {
            admitted: false,
            bedSpace: '',
            admittedAt: '',
            dischargedAt: ''
        },
        followUp: {
            required: false,
            date: '',
            notes: ''
        },
        accommodation: {
            hostel: '',
            room: ''
        }
    })

    const [validationErrors, setValidationErrors] = useState({})

    const outcomes = [
        { label: 'Treated and Returned', value: 'treated-and-returned' },
        { label: 'Under Observation', value: 'under-observation' },
        { label: 'Referred', value: 'referred' },
        { label: 'Sent Home', value: 'sent-home' },
        { label: 'Emergency Transfer', value: 'emergency-transfer' }
    ]

    const updatePayload = (values) => {
        setVisitPayload({ ...visitPayload, ...values })
    }

    const updateVitals = (values) => {
        setVisitPayload({
            ...visitPayload,
            vitals: {
                ...visitPayload.vitals,
                ...values
            }
        })
    }

    const updateAdmission = (values) => {
        setVisitPayload({
            ...visitPayload,
            admission: {
                ...visitPayload.admission,
                ...values
            }
        })
    }

    const updateFollowUp = (values) => {
        setVisitPayload({
            ...visitPayload,
            followUp: {
                ...visitPayload.followUp,
                ...values
            }
        })
    }

    const updateAccommodation = (values) => {
        setVisitPayload({
            ...visitPayload,
            accommodation: {
                ...visitPayload.accommodation,
                ...values
            }
        })
    }

    const updateSymptom = (index, value) => {
        const temp = [...visitPayload.symptoms]
        temp[index] = value
        updatePayload({ symptoms: temp })
    }

    const addSymptom = () => {
        updatePayload({ symptoms: [...visitPayload.symptoms, ''] })
    }

    const removeSymptom = (index) => {
        const temp = [...visitPayload.symptoms]
        temp.splice(index, 1)
        updatePayload({ symptoms: temp.length > 0 ? temp : [''] })
    }

    const addMedication = () => {
        const temp = [...visitPayload.medicationsDispensed]
        temp.push({
            name: '',
            dosage: '',
            quantity: '',
            instructions: '',
            notes: '',
            dispensedAt: visitPayload.visitDate || todayAsInputDate()
        })
        updatePayload({ medicationsDispensed: temp })
    }

    const updateMedication = (index, values) => {
        const temp = [...visitPayload.medicationsDispensed]
        temp[index] = {
            ...temp[index],
            ...values
        }
        updatePayload({ medicationsDispensed: temp })
    }

    const removeMedication = (index) => {
        const temp = [...visitPayload.medicationsDispensed]
        temp.splice(index, 1)
        updatePayload({ medicationsDispensed: temp })
    }

    const validateForm = () => {
        const errors = {}

        if (!visitPayload.visitDate || visitPayload.visitDate === '') {
            errors.visitDate = true
        }

        const cleanSymptoms = (visitPayload.symptoms || []).map((item) => item.trim()).filter((item) => item !== '')

        if (cleanSymptoms.length === 0) {
            errors.symptoms = true
        }

        if (!visitPayload.outcome || visitPayload.outcome === '') {
            errors.outcome = true
        }

        ;(visitPayload.medicationsDispensed || []).forEach((medication, medicationIndex) => {
            if (!medication.name || medication.name.trim() === '') {
                errors[`medication-name-${medicationIndex}`] = true
            }
            if (!medication.dispensedAt || medication.dispensedAt === '') {
                errors[`medication-dispensed-at-${medicationIndex}`] = true
            }
        })

        if (visitPayload.followUp.required && !visitPayload.followUp.date) {
            errors.followUpDate = true
        }

        setValidationErrors(errors)
        return errors
    }

    const pushSickBayRecord = () => {
        if (Object.values(validateForm()).includes(true)) {
            dispatch({
                type: ERROR,
                error: {
                    response: {
                        data: {
                            message: 'Please check the highlighted fields'
                        }
                    }
                }
            })
            return
        }

        const cleanSymptoms = (visitPayload.symptoms || []).map((item) => item.trim()).filter((item) => item !== '')

        const payload = {
            student,
            visitDate: new Date(visitPayload.visitDate),
            symptoms: cleanSymptoms,
            outcome: visitPayload.outcome
        }

        if (visitPayload.complaints && visitPayload.complaints.trim() !== '') {
            payload.complaints = visitPayload.complaints.trim()
        }

        if (visitPayload.diagnosis && visitPayload.diagnosis.trim() !== '') {
            payload.diagnosis = visitPayload.diagnosis.trim()
        }

        if (visitPayload.treatmentPlan && visitPayload.treatmentPlan.trim() !== '') {
            payload.treatmentPlan = visitPayload.treatmentPlan.trim()
        }

        const hasVitals =
            visitPayload.vitals.temperature ||
            visitPayload.vitals.pulseRate ||
            (visitPayload.vitals.bloodPressure && visitPayload.vitals.bloodPressure.trim() !== '') ||
            visitPayload.vitals.weight ||
            (visitPayload.vitals.notes && visitPayload.vitals.notes.trim() !== '')

        if (hasVitals) {
            payload.vitals = {
                ...(visitPayload.vitals.temperature ? { temperature: visitPayload.vitals.temperature } : {}),
                ...(visitPayload.vitals.pulseRate ? { pulseRate: visitPayload.vitals.pulseRate } : {}),
                ...(visitPayload.vitals.bloodPressure && visitPayload.vitals.bloodPressure.trim() !== ''
                    ? { bloodPressure: visitPayload.vitals.bloodPressure.trim() }
                    : {}),
                ...(visitPayload.vitals.weight ? { weight: visitPayload.vitals.weight } : {}),
                ...(visitPayload.vitals.notes && visitPayload.vitals.notes.trim() !== ''
                    ? { notes: visitPayload.vitals.notes.trim() }
                    : {})
            }
        }

        if (visitPayload.medicationsDispensed.length > 0) {
            payload.medicationsDispensed = visitPayload.medicationsDispensed.map((medication) => ({
                name: medication.name,
                ...(medication.dosage && medication.dosage.trim() !== '' ? { dosage: medication.dosage.trim() } : {}),
                ...(medication.quantity && medication.quantity.trim() !== '' ? { quantity: medication.quantity.trim() } : {}),
                ...(medication.instructions && medication.instructions.trim() !== ''
                    ? { instructions: medication.instructions.trim() }
                    : {}),
                ...(medication.notes && medication.notes.trim() !== '' ? { notes: medication.notes.trim() } : {}),
                dispensedAt: new Date(medication.dispensedAt)
            }))
        }

        const hasAdmissionData =
            visitPayload.admission.admitted ||
            (visitPayload.admission.bedSpace && visitPayload.admission.bedSpace.trim() !== '') ||
            visitPayload.admission.admittedAt ||
            visitPayload.admission.dischargedAt

        if (hasAdmissionData) {
            payload.admission = {
                admitted: visitPayload.admission.admitted,
                ...(visitPayload.admission.bedSpace && visitPayload.admission.bedSpace.trim() !== ''
                    ? { bedSpace: visitPayload.admission.bedSpace.trim() }
                    : {}),
                ...(visitPayload.admission.admittedAt ? { admittedAt: new Date(visitPayload.admission.admittedAt) } : {}),
                ...(visitPayload.admission.dischargedAt
                    ? { dischargedAt: new Date(visitPayload.admission.dischargedAt) }
                    : {})
            }
        }

        const hasFollowUpData =
            visitPayload.followUp.required ||
            visitPayload.followUp.date ||
            (visitPayload.followUp.notes && visitPayload.followUp.notes.trim() !== '')

        if (hasFollowUpData) {
            payload.followUp = {
                required: visitPayload.followUp.required,
                ...(visitPayload.followUp.date ? { date: new Date(visitPayload.followUp.date) } : {}),
                ...(visitPayload.followUp.notes && visitPayload.followUp.notes.trim() !== ''
                    ? { notes: visitPayload.followUp.notes.trim() }
                    : {})
            }
        }

        const hasAccommodationData =
            (visitPayload.accommodation.hostel && visitPayload.accommodation.hostel.trim() !== '') ||
            (visitPayload.accommodation.room && visitPayload.accommodation.room.trim() !== '')

        if (hasAccommodationData) {
            payload.accommodation = {
                ...(visitPayload.accommodation.hostel && visitPayload.accommodation.hostel.trim() !== ''
                    ? { hostel: visitPayload.accommodation.hostel.trim() }
                    : {}),
                ...(visitPayload.accommodation.room && visitPayload.accommodation.room.trim() !== ''
                    ? { room: visitPayload.accommodation.room.trim() }
                    : {})
            }
        }

        dispatch(createSickBayRecord(payload))
    }

    return (
        <div className='w-full'>
            <div className='w-full'>
                <DateField
                    requiredField={true}
                    inputLabel='Visit date'
                    fieldId='visit-date'
                    preloadValue={visitPayload.visitDate}
                    hasError={validationErrors.visitDate}
                    returnFieldValue={(value) => updatePayload({ visitDate: value })}
                />
            </div>

            <div className='mt-6 border-t pt-6'>
                <h3 className='text-sm uppercase tracking-[0.2em] text-gray-500 mb-4'>Vitals</h3>
                <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4'>
                    <NumberField
                        inputLabel='Temperature (C)'
                        fieldId='vitals-temperature'
                        preloadValue={visitPayload.vitals.temperature}
                        hasError={false}
                        inputPlaceholder='e.g. 36.5'
                        returnFieldValue={(value) => updateVitals({ temperature: value })}
                    />
                    <NumberField
                        inputLabel='Pulse rate'
                        fieldId='vitals-pulse-rate'
                        preloadValue={visitPayload.vitals.pulseRate}
                        hasError={false}
                        inputPlaceholder='e.g. 80'
                        returnFieldValue={(value) => updateVitals({ pulseRate: value })}
                    />
                    <TextField
                        inputLabel='Blood pressure'
                        fieldId='vitals-blood-pressure'
                        preloadValue={visitPayload.vitals.bloodPressure}
                        hasError={false}
                        inputPlaceholder='e.g. 120/80'
                        returnFieldValue={(value) => updateVitals({ bloodPressure: value })}
                    />
                    <NumberField
                        inputLabel='Weight (kg)'
                        fieldId='vitals-weight'
                        preloadValue={visitPayload.vitals.weight}
                        hasError={false}
                        inputPlaceholder='e.g. 60'
                        returnFieldValue={(value) => updateVitals({ weight: value })}
                    />
                </div>
                <div className='mt-4'>
                    <TextareaField
                        inputLabel='Vitals notes'
                        fieldId='vitals-notes'
                        inputPlaceholder='Any additional notes about the vitals'
                        preloadValue={visitPayload.vitals.notes}
                        hasError={false}
                        returnFieldValue={(value) => updateVitals({ notes: value })}
                    />
                </div>
            </div>

            <div className='mt-6'>
                <TextareaField
                    inputLabel='Complaints'
                    fieldId='complaints'
                    preloadValue={visitPayload.complaints}
                    hasError={false}
                    inputPlaceholder='Describe any complaints the student has'
                    returnFieldValue={(value) => updatePayload({ complaints: value })}
                />
            </div>

            <div className='mt-6'>
                <h3 className='text-sm uppercase tracking-[0.2em] text-gray-500 mb-2'>Symptoms</h3>
                {visitPayload.symptoms.map((symptom, symptomIndex) => (
                    <div key={symptomIndex} className='w-full relative mb-4'>
                        {symptomIndex > 0 && (
                            <div className='absolute top-0 right-0 z-20'>
                                <button onClick={() => removeSymptom(symptomIndex)} className='text-gray-500 hover:text-red-600 transition duration-200'>
                                    <TrashIcon className='w-5 h-5' />
                                </button>
                            </div>
                        )}
                        <TextField
                            inputLabel={`Symptom ${symptomIndex + 1}`}
                            fieldId={`symptom-${symptomIndex}`}
                            requiredField={true}
                            preloadValue={symptom}
                            inputPlaceholder={`Describe symptom ${symptomIndex + 1}`}
                            hasError={validationErrors.symptoms}
                            returnFieldValue={(value) => updateSymptom(symptomIndex, value)}
                        />
                    </div>
                ))}
                <div className='flex flex-row-reverse'>
                    <button onClick={addSymptom} className='text-gray-300 bg-vcm-dark-gray p-2 rounded transition duration-200 hover:bg-vcm-light-purple hover:text-vcm-dark-gray text-sm flex items-center gap-x-2'>
                        <PlusIcon className='w-5 h-5' /> Add symptom
                    </button>
                </div>
            </div>

            <div className='mt-4'>
                <TextareaField
                    inputLabel='Diagnosis'
                    fieldId='diagnosis'
                    inputPlaceholder='Describe the diagnosis'
                    preloadValue={visitPayload.diagnosis}
                    hasError={false}
                    returnFieldValue={(value) => updatePayload({ diagnosis: value })}
                />
            </div>

            <div className='mt-4'>
                <TextareaField
                    inputLabel='Treatment plan'
                    fieldId='treatment-plan'
                    preloadValue={visitPayload.treatmentPlan}
                    hasError={false}
                    inputPlaceholder='Describe the treatment plan'
                    returnFieldValue={(value) => updatePayload({ treatmentPlan: value })}
                />
            </div>

            <div className='mt-6 border-t pt-6'>
                <div className='flex items-center justify-between mb-4'>
                    <h3 className='text-sm uppercase tracking-[0.2em] text-gray-500'>Medications dispensed</h3>
                    <button onClick={addMedication} className='text-gray-300 bg-vcm-dark-gray p-2 rounded transition duration-200 hover:bg-vcm-light-purple hover:text-vcm-dark-gray text-sm flex items-center gap-x-2'>
                        <PlusIcon className='w-5 h-5' /> Add medication
                    </button>
                </div>

                {visitPayload.medicationsDispensed.map((medication, medicationIndex) => (
                    <div key={medicationIndex} className='mb-5 p-4 border rounded relative'>
                        <div className='absolute top-3 right-3'>
                            <button onClick={() => removeMedication(medicationIndex)} className='text-gray-500 hover:text-red-600 transition duration-200'>
                                <TrashIcon className='w-5 h-5' />
                            </button>
                        </div>
                        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                            <TextField
                                inputLabel='Medication name'
                                fieldId={`medication-name-${medicationIndex}`}
                                requiredField={true}
                                preloadValue={medication.name}
                                hasError={validationErrors[`medication-name-${medicationIndex}`]}
                                returnFieldValue={(value) => updateMedication(medicationIndex, { name: value })}
                            />
                            <TextField
                                inputLabel='Dosage'
                                fieldId={`medication-dosage-${medicationIndex}`}
                                preloadValue={medication.dosage}
                                hasError={false}
                                returnFieldValue={(value) => updateMedication(medicationIndex, { dosage: value })}
                            />
                            <TextField
                                inputLabel='Quantity'
                                fieldId={`medication-quantity-${medicationIndex}`}
                                preloadValue={medication.quantity}
                                hasError={false}
                                returnFieldValue={(value) => updateMedication(medicationIndex, { quantity: value })}
                            />
                            <DateField
                                inputLabel='Dispensed at'
                                fieldId={`medication-dispensed-at-${medicationIndex}`}
                                requiredField={true}
                                preloadValue={medication.dispensedAt}
                                hasError={validationErrors[`medication-dispensed-at-${medicationIndex}`]}
                                returnFieldValue={(value) => updateMedication(medicationIndex, { dispensedAt: value })}
                            />
                        </div>
                        <div className='mt-4'>
                            <TextareaField
                                inputLabel='Instructions'
                                fieldId={`medication-instructions-${medicationIndex}`}
                                preloadValue={medication.instructions}
                                hasError={false}
                                returnFieldValue={(value) => updateMedication(medicationIndex, { instructions: value })}
                            />
                        </div>
                        <div className='mt-4'>
                            <TextareaField
                                inputLabel='Notes'
                                fieldId={`medication-notes-${medicationIndex}`}
                                preloadValue={medication.notes}
                                hasError={false}
                                returnFieldValue={(value) => updateMedication(medicationIndex, { notes: value })}
                            />
                        </div>
                    </div>
                ))}
            </div>

            <div className='mt-6 border-t pt-6'>
                <div className='w-full flex justify-between gap-x-4'>
                    <div className='w-10/12'>
                        <label className='block text-sm font-medium text-gray-700'>Admission</label>
                        <label className='text-xs text-gray-500'>Was this student admitted for observation?</label>
                    </div>
                    <div className='w-2/12 flex flex-row-reverse'>
                        <Switch
                            checked={visitPayload.admission.admitted}
                            onChange={() => updateAdmission({ admitted: !visitPayload.admission.admitted })}
                            className={`${visitPayload.admission.admitted ? 'bg-vcm-light-purple' : 'bg-gray-400'} relative inline-flex items-center h-5 rounded-full w-10`}
                        >
                            <span className='sr-only'>Admission toggle</span>
                            <span
                                className={`transform transition ease-in-out duration-200 ${visitPayload.admission.admitted ? 'translate-x-6 bg-vcm-purple' : 'translate-x-1 bg-gray-600'} inline-block w-3 h-3 transform rounded-full`}
                            />
                        </Switch>
                    </div>
                </div>

                <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mt-4'>
                    <TextField
                        inputLabel='Bed space'
                        fieldId='admission-bed-space'
                        preloadValue={visitPayload.admission.bedSpace}
                        hasError={false}
                        returnFieldValue={(value) => updateAdmission({ bedSpace: value })}
                    />
                    <DateField
                        inputLabel='Admitted at'
                        fieldId='admission-admitted-at'
                        preloadValue={visitPayload.admission.admittedAt}
                        hasError={false}
                        returnFieldValue={(value) => updateAdmission({ admittedAt: value })}
                    />
                    <DateField
                        inputLabel='Discharged at'
                        fieldId='admission-discharged-at'
                        preloadValue={visitPayload.admission.dischargedAt}
                        hasError={false}
                        returnFieldValue={(value) => updateAdmission({ dischargedAt: value })}
                    />
                </div>
            </div>

            <div className='mt-6 border-t pt-6'>
                <div className='w-full flex justify-between gap-x-4'>
                    <div className='w-10/12'>
                        <label className='block text-sm font-medium text-gray-700'>Follow up</label>
                        <label className='text-xs text-gray-500'>Should this student return for a follow-up check?</label>
                    </div>
                    <div className='w-2/12 flex flex-row-reverse'>
                        <Switch
                            checked={visitPayload.followUp.required}
                            onChange={() => updateFollowUp({ required: !visitPayload.followUp.required })}
                            className={`${visitPayload.followUp.required ? 'bg-vcm-light-purple' : 'bg-gray-400'} relative inline-flex items-center h-5 rounded-full w-10`}
                        >
                            <span className='sr-only'>Follow-up toggle</span>
                            <span
                                className={`transform transition ease-in-out duration-200 ${visitPayload.followUp.required ? 'translate-x-6 bg-vcm-purple' : 'translate-x-1 bg-gray-600'} inline-block w-3 h-3 transform rounded-full`}
                            />
                        </Switch>
                    </div>
                </div>

                <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mt-4'>
                    <DateField
                        inputLabel='Follow-up date'
                        fieldId='followup-date'
                        requiredField={visitPayload.followUp.required}
                        preloadValue={visitPayload.followUp.date}
                        hasError={validationErrors.followUpDate}
                        returnFieldValue={(value) => updateFollowUp({ date: value })}
                    />
                    <TextField
                        inputLabel='Hostel'
                        fieldId='accommodation-hostel'
                        preloadValue={visitPayload.accommodation.hostel}
                        hasError={false}
                        returnFieldValue={(value) => updateAccommodation({ hostel: value })}
                    />
                    <TextField
                        inputLabel='Room'
                        fieldId='accommodation-room'
                        preloadValue={visitPayload.accommodation.room}
                        hasError={false}
                        returnFieldValue={(value) => updateAccommodation({ room: value })}
                    />
                </div>

                <div className='mt-4'>
                    <TextareaField
                        inputLabel='Follow-up notes'
                        fieldId='followup-notes'
                        preloadValue={visitPayload.followUp.notes}
                        hasError={false}
                        returnFieldValue={(value) => updateFollowUp({ notes: value })}
                    />
                </div>
            </div>

            <div className='mt-4'>
                <SelectField
                    selectOptions={outcomes}
                    inputLabel='Outcome'
                    titleField='label'
                    displayImage={false}
                    imageField=''
                    preSelectedIndex={outcomes.findIndex((item) => item.value === visitPayload.outcome)}
                    hasError={validationErrors.outcome}
                    returnFieldValue={(value) => updatePayload({ outcome: value.value })}
                />
            </div>

            <div className='mt-8 flex flex-row-reverse gap-x-4'>
                <ModalActionButton
                    buttonLabel='Record Sickbay Visit'
                    buttonAction={pushSickBayRecord}
                    processing={sickbaySelector.creatingSickBayRecord}
                />

                <button
                    type='button'
                    className='inline-flex justify-center px-5 py-4 text-xs font-medium bg-transparent border border-transparent rounded-md hover:bg-opacity-50 hover:bg-secondary focus:outline-none'
                    onClick={close}
                >
                    Cancel
                </button>
            </div>
        </div>
    )
}

export default NewSickbayVisit
